import { useState, useEffect } from 'react'
import { TrendingUp, Users, BarChart3 } from 'lucide-react'
import { authService } from '../../services/auth'
import { fetchAuthSession } from 'aws-amplify/auth'

interface MarketingData {
  utm_source: Record<string, number>
  utm_medium: Record<string, number>
  utm_campaign: Record<string, number>
  utm_content: Record<string, number>
  utm_term: Record<string, number>
  referral_source: Record<string, number>
  total_users: number
  users_with_attribution: number
}

export function MarketingAttribution() {
  const [data, setData] = useState<MarketingData | null>(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    loadMarketingData()
  }, [])

  const loadMarketingData = async () => {
    try {
      let token = authService.getToken()
      
      if (!token) {
        const session = await fetchAuthSession()
        token = session.tokens?.idToken?.toString() || null
        if (token) authService.setToken(token)
      }

      const url = `${(import.meta as any).env.VITE_API_URL.replace(/\/$/, "")}/api/admin/marketing-attribution`
      console.log('Fetching marketing data from:', url)
      
      const response = await fetch(url, { 
        headers: { 'Authorization': `Bearer ${token}` } 
      })

      console.log('Marketing API response status:', response.status)
      
      if (response.ok) {
        const result = await response.json()
        console.log('Marketing data received:', result)
        setData(result)
      } else {
        console.error('Marketing API error:', response.status, await response.text())
      }
    } catch (error) {
      console.error('Failed to load marketing data:', error)
    } finally {
      setLoading(false)
    }
  }

  const getTopN = (obj: Record<string, number>, n: number = 3) => {
    return Object.entries(obj)
      .sort(([, a], [, b]) => b - a)
      .slice(0, n)
  }

  if (loading) {
    return (
      <div className="bg-white rounded-lg shadow p-6">
        <div className="animate-pulse">
          <div className="h-6 bg-gray-200 rounded w-1/3 mb-4"></div>
          <div className="space-y-3">
            <div className="h-4 bg-gray-200 rounded"></div>
            <div className="h-4 bg-gray-200 rounded w-5/6"></div>
          </div>
        </div>
      </div>
    )
  }

  // Always show the component, even with no data
  const topSources = data ? getTopN(data.utm_source) : []
  const topReferrals = data ? getTopN(data.referral_source) : []
  const topCampaigns = data ? getTopN(data.utm_campaign) : []

  return (
    <div className="bg-white rounded-lg shadow p-6">
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-xl font-semibold flex items-center">
          <TrendingUp className="h-5 w-5 mr-2 text-blue-600" />
          Marketing Attribution
        </h2>
        <div className="text-sm text-gray-600">
          {data ? `${data.users_with_attribution} of ${data.total_users} users tracked` : 'Loading...'}
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {/* Top UTM Sources */}
        <div className="border rounded-lg p-4">
          <h3 className="text-sm font-semibold text-gray-700 mb-3 flex items-center">
            <BarChart3 className="h-4 w-4 mr-2" />
            Top Traffic Sources
          </h3>
          {topSources.length > 0 ? (
            <div className="space-y-2">
              {topSources.map(([source, count], idx) => (
                <div key={source} className="flex items-center justify-between">
                  <div className="flex items-center">
                    <span className="text-lg mr-2">{idx === 0 ? '🥇' : idx === 1 ? '🥈' : '🥉'}</span>
                    <span className="text-sm font-medium capitalize">{source}</span>
                  </div>
                  <span className="text-sm font-semibold text-blue-600">{count}</span>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-sm text-gray-500">No data yet</p>
          )}
        </div>

        {/* Top Referral Sources */}
        <div className="border rounded-lg p-4">
          <h3 className="text-sm font-semibold text-gray-700 mb-3 flex items-center">
            <Users className="h-4 w-4 mr-2" />
            Top Referral Sources
          </h3>
          {topReferrals.length > 0 ? (
            <div className="space-y-2">
              {topReferrals.map(([source, count], idx) => (
                <div key={source} className="flex items-center justify-between">
                  <div className="flex items-center">
                    <span className="text-lg mr-2">{idx === 0 ? '🥇' : idx === 1 ? '🥈' : '🥉'}</span>
                    <span className="text-sm font-medium capitalize">{source.replace(/-/g, ' ')}</span>
                  </div>
                  <span className="text-sm font-semibold text-green-600">{count}</span>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-sm text-gray-500">No data yet</p>
          )}
        </div>

        {/* Top Campaigns */}
        <div className="border rounded-lg p-4">
          <h3 className="text-sm font-semibold text-gray-700 mb-3 flex items-center">
            <TrendingUp className="h-4 w-4 mr-2" />
            Top Campaigns
          </h3>
          {topCampaigns.length > 0 ? (
            <div className="space-y-2">
              {topCampaigns.map(([campaign, count], idx) => (
                <div key={campaign} className="flex items-center justify-between">
                  <div className="flex items-center">
                    <span className="text-lg mr-2">{idx === 0 ? '🥇' : idx === 1 ? '🥈' : '🥉'}</span>
                    <span className="text-sm font-medium capitalize">{campaign}</span>
                  </div>
                  <span className="text-sm font-semibold text-purple-600">{count}</span>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-sm text-gray-500">No data yet</p>
          )}
        </div>
      </div>

      {/* All UTM Mediums Table */}
      {data && Object.keys(data.utm_medium).length > 0 && (
        <div className="mt-6 border rounded-lg p-4">
          <h3 className="text-sm font-semibold text-gray-700 mb-3">Traffic Mediums</h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {Object.entries(data.utm_medium)
              .sort(([, a], [, b]) => b - a)
              .map(([medium, count]) => (
                <div key={medium} className="flex justify-between items-center bg-gray-50 rounded px-3 py-2">
                  <span className="text-xs font-medium capitalize">{medium}</span>
                  <span className="text-xs font-semibold text-gray-700">{count}</span>
                </div>
              ))}
          </div>
        </div>
      )}

      {/* A/B Testing - UTM Content */}
      {data && Object.keys(data.utm_content).length > 0 && (
        <div className="mt-6 border rounded-lg p-4">
          <h3 className="text-sm font-semibold text-gray-700 mb-3">A/B Test Performance (Content)</h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {Object.entries(data.utm_content)
              .sort(([, a], [, b]) => b - a)
              .map(([content, count]) => (
                <div key={content} className="flex justify-between items-center bg-blue-50 rounded px-3 py-2">
                  <span className="text-xs font-medium">{content}</span>
                  <span className="text-xs font-semibold text-blue-700">{count}</span>
                </div>
              ))}
          </div>
        </div>
      )}

      {/* Keyword Performance - UTM Term */}
      {data && Object.keys(data.utm_term).length > 0 && (
        <div className="mt-6 border rounded-lg p-4">
          <h3 className="text-sm font-semibold text-gray-700 mb-3">Keyword Performance (Terms)</h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {Object.entries(data.utm_term)
              .sort(([, a], [, b]) => b - a)
              .map(([term, count]) => (
                <div key={term} className="flex justify-between items-center bg-green-50 rounded px-3 py-2">
                  <span className="text-xs font-medium">{term}</span>
                  <span className="text-xs font-semibold text-green-700">{count}</span>
                </div>
              ))}
          </div>
        </div>
      )}
    </div>
  )
}
